
import { Card } from "@/components/ui/card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import DeleteUserDialog from "./DeleteUserDialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import AddUserFromResumeModal from "./AddUserFromResumeModal";
import { AddUserDialog } from "./AddUserDialog";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";

type User = {
  id: string;
  email: string;
  role: 'admin' | 'user';
  created_at: string;
  email_sent?: boolean;
};

export const UserList = () => {
  const queryClient = useQueryClient();
  const { data: users, isFetching } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data: { users: authUsers }, error } = await supabase.functions.invoke('list-users');
      if (error) throw error;

      // Fetch all user roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');
      if (rolesError) throw rolesError;

      // Fetch developer statuses
      const { data: statuses, error: statusError } = await supabase
        .from('developer_status')
        .select('user_id, email_sent');
      if (statusError) throw statusError;

      // Create maps for quick lookup
      const roleMap = new Map(userRoles?.map(ur => [ur.user_id, ur.role]));
      const statusMap = new Map(statuses?.map(status => [status.user_id, status.email_sent]));

      // Combine the data
      return authUsers.map((user: any) => ({
        id: user.id,
        email: user.email,
        role: roleMap.get(user.id) || 'user',
        created_at: user.created_at,
        email_sent: statusMap.get(user.id) || false
      }));
    },
  });

  const { data: testSettings } = useQuery({
    queryKey: ["testSettings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("candidate_test_settings")
        .select("*")
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { toast } = useToast();

  const sendEmailMutation = useMutation({
    mutationFn: async ({ userId, userEmail }: { userId: string; userEmail: string }) => {
      const { error } = await supabase.functions.invoke('send-test-email', {
        body: { 
          userId,
          userEmail,
          testUrl: testSettings?.test_url
        }
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "Success",
        description: "Test email has been sent successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to send test email.",
        variant: "destructive",
      });
      console.error("Error sending email:", error);
    },
  });

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">User Administration</h2>
          <div className="text-sm text-muted-foreground">
            Total users: {users?.length || 0}
          </div>
        </div>
        <div className="flex gap-2">
          <AddUserFromResumeModal />
          <AddUserDialog />
        </div>
      </div>
      {isFetching ? (
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-12 bg-gray-200 rounded" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4 font-semibold pb-2 border-b">
            <div>Email</div>
            <div>Role</div>
            <div>Created At</div>
            <div>Actions</div>
          </div>
          <ScrollArea className="h-[60vh]">
            <div className="space-y-2">
              {users?.map((user) => (
                <div key={user.id} className="grid grid-cols-4 gap-4 py-2 border-b">
                  <div className="flex items-center">{user.email || "N/A"}</div>
                  <div className="capitalize flex items-center">{user.role || "N/A"}</div>
                  <div className="capitalize flex items-center">{new Date(user.created_at).toLocaleDateString()}</div>
                  <div className="capitalize flex items-center">
                    <div className="flex gap-2">
                      <Button 
                        variant={user.email_sent ? "default" : "outline"}
                        size="sm" 
                        onClick={() => sendEmailMutation.mutate({ 
                          userId: user.id, 
                          userEmail: user.email 
                        })}
                        disabled={!testSettings?.test_url || sendEmailMutation.isPending}
                        className={user.email_sent ? "bg-green-500 hover:bg-green-600" : ""}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <DeleteUserDialog userId={user.id} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </Card>
  );
};
